import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { projectAPI, purchaseOrderAPI, customerAPI } from '../services/api';
import type { Project, PurchaseOrder, Customer } from '../types';

const ProjectForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<Partial<Project>>({
    customer: null,
    name: '',
    code: '',
    description: '',
    is_active: true,
  });
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [linkedPOs, setLinkedPOs] = useState<PurchaseOrder[]>([]);

  useEffect(() => {
    fetchCustomers();
    if (isEditMode && id) {
      fetchProject(parseInt(id));
    }
  }, [id, isEditMode]);

  const fetchCustomers = async () => {
    try {
      const response = await customerAPI.getAll();
      setCustomers(response.data.results);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchProject = async (projectId: number) => {
    try {
      const response = await projectAPI.getById(projectId);
      setFormData(response.data);
      fetchLinkedPOs(projectId);
    } catch (error) {
      console.error('Failed to fetch project:', error);
    }
  };

  const fetchLinkedPOs = async (projectId: number) => {
    try {
      const response = await purchaseOrderAPI.getAll();
      const filtered = response.data.results.filter(
        (po: PurchaseOrder) => po.projects && po.projects.includes(projectId)
      );
      setLinkedPOs(filtered);
    } catch (error) {
      console.error('Failed to fetch linked POs:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isEditMode && id) {
        await projectAPI.update(parseInt(id), formData);
      } else {
        await projectAPI.create(formData);
      }
      navigate('/projects');
    } catch (error) {
      console.error('Failed to save project:', error);
      alert('Failed to save project');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    let processedValue: string | boolean | number | null = value;
    
    if (type === 'checkbox') {
      processedValue = checked;
    } else if (name === 'customer') {
      processedValue = value === '' ? null : parseInt(value, 10);
    }
    
    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));
  };

  return (
    <div className="container">
      <div className="card">
        <h2>{isEditMode ? 'Edit Project' : 'Create New Project'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="customer">Company</label>
            <select
              id="customer"
              name="customer"
              value={formData.customer ?? ''}
              onChange={handleChange}
            >
              <option value="">-- No Company --</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="code">Project Code *</label>
            <input
              type="text"
              id="code"
              name="code"
              value={formData.code}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="name">Project Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
            />
          </div>

          <div className="form-group">
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                style={{ cursor: 'pointer' }}
              />
              <span>Active</span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: '1rem' }}>
            <button type="submit" className="btn btn-primary">
              {isEditMode ? 'Update Project' : 'Create Project'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/projects')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      {isEditMode && (
        <div className="card" style={{ marginTop: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3>Purchase Orders for this Project</h3>
            <Link
              to={`/purchase-orders/new?project=${id}`}
              className="btn btn-primary btn-small"
            >
              + Create PO
            </Link>
          </div>
          
          {linkedPOs.length === 0 ? (
            <p style={{ color: '#7f8c8d' }}>
              No purchase orders linked to this project yet. Create one to get started.
            </p>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>PO Number</th>
                    <th>Company</th>
                    <th>Issue Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {linkedPOs.map((po) => (
                    <tr key={po.id}>
                      <td>{po.po_number}</td>
                      <td>{po.customer_details?.name || `Company #${po.customer}`}</td>
                      <td>{po.issue_date}</td>
                      <td>
                        <span className={`status-badge status-${po.status}`}>
                          {po.status}
                        </span>
                      </td>
                      <td className="actions">
                        <Link
                          to={`/purchase-orders/${po.id}/edit`}
                          className="btn btn-secondary btn-small"
                        >
                          Edit
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectForm;

